const peso = (value) => `₱${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function FinancialReport({ report, generatedAt, view = 'summary' }) {
  const {
    periodLabel,
    collectionsByCategory,
    totalCollections,
    disbursementRows,
    totalDisbursements,
    netBalance,
  } = report;

  const showCollections = view === 'collections' || view === 'summary';
  const showDisbursements = view === 'disbursement' || view === 'summary';
  const showSummaryTotals = view === 'summary';
  const collectionsHeading = view === 'summary' ? 'I. Collections' : 'Collections';
  const disbursementsHeading = view === 'summary' ? 'II. Disbursements' : 'Disbursements';

  return (
    <div className="financial-report bg-white text-ink">
      <div className="flex justify-center gap-4 border-b-2 border-navy pb-3">
        <img src="/images/cnhs512.png" alt="School Logo" className="h-16 w-16 object-contain" />
        <div className="text-center">
          <p className="text-xs font-bold tracking-wide">CALAUAG NATIONAL HIGH SCHOOL</p>
          <h1 className="text-lg font-bold">School Parent-Teacher Association (SPTA)</h1>
          <h2 className="text-base font-semibold">Financial Statement / Report</h2>
          <p className="text-sm text-muted mt-1">{periodLabel}</p>
        </div>
        <div className="h-16 w-16" aria-hidden="true" />
      </div>

      <p className="text-xs text-muted text-right mt-2">
        Generated on {generatedAt.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>

      {showCollections && (
      <section className="mt-4">
        <h3 className="text-sm font-bold uppercase border-b border-gray-300 pb-1 mb-2">{collectionsHeading}</h3>
        <table className="w-full text-[10pt] leading-[10pt] border-collapse">
          <thead>
            <tr className="border-b border-gray-300 text-left">
              <th className="py-1.5 pr-2 font-semibold">Category</th>
              <th className="py-1.5 pl-2 font-semibold text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {collectionsByCategory.length === 0 ? (
              <tr>
                <td colSpan={2} className="py-3 text-center text-muted">
                  No collections for this period
                </td>
              </tr>
            ) : (
              collectionsByCategory.map((row) => (
                <tr key={row.category} className="border-b border-gray-100">
                  <td className="py-1.5 pr-2">{row.category}</td>
                  <td className="py-1.5 pl-2 text-right">{peso(row.amount)}</td>
                </tr>
              ))
            )}
          </tbody>
          <br></br>
          <tfoot>
            <tr className="border-t-2 border-navy font-bold">
              <td className="py-2 pb-2 pr-2">Total Collections</td>
              <td className="py-2 pb-2 pl-2 text-right">{peso(totalCollections)}</td>
            </tr>
          </tfoot>
        </table>
      </section>
      )}

      {showDisbursements && (
      <section className="mt-5">
        <h3 className="text-sm font-bold uppercase border-b border-gray-300 pb-1 mb-2">{disbursementsHeading}</h3>
        <table className="w-full text-[10pt] leading-[10pt] border-collapse">
          <thead>
            <tr className="border-b border-gray-300 text-left">
              <th className="py-1.5 pr-2 font-semibold whitespace-nowrap">Date</th>
              <th className="py-1.5 px-2 font-semibold">Particulars</th>
              <th className="py-1.5 px-2 font-semibold">Category</th>
              <th className="py-1.5 px-2 font-semibold whitespace-nowrap">Voucher #</th>
              <th className="py-1.5 px-2 font-semibold">Recipient</th>
              <th className="py-1.5 px-2 font-semibold whitespace-nowrap">Status</th>
              <th className="py-1.5 pl-2 font-semibold text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {disbursementRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-3 text-center text-muted">
                  No disbursements for this period
                </td>
              </tr>
            ) : (
              disbursementRows.map((row, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-1.5 pr-2 whitespace-nowrap">{row.date || '—'}</td>
                  <td className="py-1.5 px-2">{row.name}</td>
                  <td className="py-1.5 px-2">{row.category || '—'}</td>
                  <td className="py-1.5 px-2 whitespace-nowrap">{row.voucherNumber || '—'}</td>
                  <td className="py-1.5 px-2">{row.recipient || '—'}</td>
                  <td className="py-1.5 px-2 whitespace-nowrap">{row.status}</td>
                  <td className="py-1.5 pl-2 text-right whitespace-nowrap">{peso(row.amount)}</td>
                </tr>
              ))
            )}
          </tbody>
          <br></br>
          <tfoot>
            <tr className="border-t-2 border-navy font-bold">
              <td colSpan={6} className="py-2 pb-2 pr-2">
                Total Disbursements
              </td>
              <td className="py-2 pb-2 pl-2 text-right">{peso(totalDisbursements)}</td>
            </tr>
          </tfoot>
        </table>
      </section>
      )}

      {showSummaryTotals && (
      <section className="mt-5">
        <h3 className="text-sm font-bold uppercase border-b border-gray-300 pb-1 mb-2">III. Summary</h3>
        <table className="w-full text-sm border-collapse max-w-sm ml-auto">
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="py-1.5 pr-2">Total Collections</td>
              <td className="py-1.5 pl-2 text-right">{peso(totalCollections)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className="py-1.5 pr-2">Total Disbursements</td>
              <td className="py-1.5 pl-2 text-right">({peso(totalDisbursements)})</td>
            </tr>
            <br></br>
            <tr className="border-t-2 border-navy font-bold">
              <td className="py-2 pr-2">Net Balance</td>
              <td className="py-2 pl-2 text-right">{peso(netBalance)}</td>
            </tr>
          </tbody>
        </table>
      </section>
      )}
    </div>
  );
}

export default FinancialReport;
